import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import SectionTitle from '@/components/base/SectionTitle';
import { useSeo } from '@/hooks/useSeo';
import { getMediaById, getRelated, typeLabel } from '@/utils/media';
import PlayModal from './components/PlayModal';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = getMediaById(id ?? '');
  const [showPlay, setShowPlay] = useState(false);

  useSeo({
    title: item ? `${item.title} - 详情 - 天堂影视` : '影视信息不存在 - 天堂影视',
    description: item
      ? `${item.title}（${item.year}）剧情简介：${item.synopsis.slice(0, 60)}…… 类型${item.genres.join('、')}，导演${item.director}，天堂影视为你提供详细资料与相关推荐。`
      : '未找到对应的影视信息，请返回天堂影视首页浏览更多精彩内容。',
  });

  if (!item) {
    return (
      <div className="container-page py-16">
        <EmptyState
          icon="ri-error-warning-line"
          title="影视信息不存在"
          description="你访问的影视可能已下线或链接有误，返回影视库看看其他精彩内容吧。"
        />
        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelated(item, 4);
  const backTo = `/category/${item.type}`;

  const infoRows = [
    { label: '类型', value: item.genres.join(' / '), icon: 'ri-price-tag-3-line' },
    { label: '导演', value: item.director, icon: 'ri-user-star-line' },
    { label: '主演', value: item.actors.join('、'), icon: 'ri-group-line' },
    { label: '年份', value: `${item.year}年`, icon: 'ri-calendar-line' },
    { label: '地区', value: item.region, icon: 'ri-map-pin-line' },
    { label: '片长', value: item.duration, icon: 'ri-time-line' },
    { label: '评分', value: `${item.rating.toFixed(1)} 分`, icon: 'ri-star-line' },
    { label: '更新', value: item.updatedAt, icon: 'ri-refresh-line' },
  ];

  return (
    <div>
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={item.backdrop}
            alt={item.title}
            title={`${item.title} - 天堂影视影视详情`}
            className="h-full w-full object-top opacity-60"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/30"></div>

        <div className="container-page relative pt-8 pb-8 md:pt-12 md:pb-10">
          <nav className="flex items-center gap-2 text-sm text-background-200/80">
            <Link to="/" className="transition-colors hover:text-accent-400 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={backTo} className="transition-colors hover:text-accent-400 cursor-pointer">
              {typeLabel(item.type)}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">{item.title}</span>
          </nav>

          <div className="mt-6 flex flex-col gap-6 md:flex-row">
            <div className="w-32 shrink-0 overflow-hidden rounded-lg border border-background-50/20 md:w-44">
              <img
                src={item.poster}
                alt={item.title}
                title={`${item.title} - 天堂影视影视海报`}
                className="aspect-[2/3] w-full object-top"
              />
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-background-50 md:text-4xl">{item.title}</h1>
              <p className="mt-2 text-sm text-accent-300 md:text-base">{item.tagline}</p>

              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-background-200">
                <span className="flex items-center gap-1 text-lg font-bold text-accent-400">
                  <i className="ri-star-fill"></i>
                  {item.rating.toFixed(1)}
                </span>
                <span className="rounded bg-background-50/15 px-2 py-0.5">
                  {item.type === 'movie' ? '电影' : '电视剧'}
                </span>
                <span>{item.year}</span>
                <span>{item.region}</span>
                <span className="hidden sm:inline">{item.duration}</span>
              </div>

              <p className="mt-4 line-clamp-3 max-w-3xl text-sm leading-relaxed text-background-200/80 md:line-clamp-none">
                {item.synopsis}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setShowPlay(true)}
                  className="flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-600 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-fill text-lg"></i>
                  在线播放演示
                </button>
                <Link
                  to={backTo}
                  className="flex items-center gap-2 rounded-md border border-background-50/30 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-lg"></i>
                  返回分类
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container-page py-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          <div className="order-2 lg:order-1">
            <section>
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-file-text-line"></i>
                </span>
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                {item.synopsis}
              </p>
            </section>

            <section className="mt-8">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-lightbulb-line"></i>
                </span>
                影视特点
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                {item.features}
              </p>
            </section>

            <section className="mt-10">
              <SectionTitle title="相关推荐" subtitle="根据类型与标签为你推荐" icon="ri-thumb-up-line" />
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {related.map((rel) => (
                  <MovieCard key={rel.id} item={rel} />
                ))}
              </div>
            </section>
          </div>

          <aside className="order-1 lg:order-2">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="text-base font-bold text-foreground-950">详细资料</h2>
              <dl className="mt-4 space-y-3">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex items-start gap-3 text-sm">
                    <dt className="flex w-16 shrink-0 items-center gap-1.5 text-foreground-500">
                      <i className={`${row.icon} text-accent-500`}></i>
                      {row.label}
                    </dt>
                    <dd className="min-w-0 flex-1 text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-5 rounded-lg border border-secondary-200 bg-secondary-50 p-5">
              <p className="flex items-center gap-2 text-sm font-semibold text-secondary-900">
                <i className="ri-information-line"></i>
                温馨提示
              </p>
              <p className="mt-2 text-sm leading-relaxed text-secondary-800/90">
                本站所有影视资料均为演示数据，在线播放功能为演示状态，正式播放接口待接入。
              </p>
            </div>
          </aside>
        </div>
      </div>

      <PlayModal open={showPlay} title={item.title} onClose={() => setShowPlay(false)} />
    </div>
  );
}